import React, { useState, useEffect } from 'react';
import { FiUserPlus, FiEdit2, FiTrash2, FiShield, FiCheckSquare, FiX } from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

const PERMISSION_GROUPS = [
  {
    title: '📊 แดชบอร์ด & รายงานหลัก (Dashboard & Reports)',
    bgColor: 'bg-blue-50/40 dark:bg-blue-950/10',
    borderColor: 'border-blue-100 dark:border-blue-900/30',
    items: [
      { id: 'dashboard', label: '📊 แดชบอร์ดแอดมิน' },
      { id: 'exec_dashboard', label: '👑 แดชบอร์ดฝ่ายบริหาร' }
    ]
  },
  {
    title: '📋 บริหารงานลูกค้า & ตารางงาน (Customer & Service)',
    bgColor: 'bg-emerald-50/40 dark:bg-emerald-950/10',
    borderColor: 'border-emerald-100 dark:border-emerald-900/30',
    items: [
      { id: 'manage_jobs', label: '📋 การจ่ายงาน & ตารางคิวงาน' },
      { id: 'inspect_jobs', label: '🔍 ตรวจสอบประเมินหน้างาน' },
      { id: 'blocked_dates', label: '📅 จัดการวันคิวเต็ม (Slots)' },
      { id: 'customers', label: '👥 ฐานข้อมูลลูกค้า' }
    ]
  },
  {
    title: '💰 ระบบการเงิน, สต๊อก & ส่งออก (Finance, Stock & Export)',
    bgColor: 'bg-indigo-50/40 dark:bg-indigo-950/10',
    borderColor: 'border-indigo-100 dark:border-indigo-900/30',
    items: [
      { id: 'finance', label: '💰 ระบบการเงิน (Finance)' },
      { id: 'invoice', label: '🧾 ออกใบเสร็จ/ใบกำกับภาษี' },
      { id: 'products', label: '❄️ สต๊อกสินค้าแอร์บ้าน' },
      { id: 'sales_data', label: '📈 ข้อมูลการขาย/สต๊อกเซลส์' },
      { id: 'export_data', label: '📥 ส่งออกข้อมูล (Excel/PDF)' },
      { id: 'google_drive', label: '📁 จัดการ Google Drive' }
    ]
  },
  {
    title: '🌐 จัดการหน้าเว็บ & แชทบริการ (CMS & Service Chat)',
    bgColor: 'bg-purple-50/40 dark:bg-purple-950/10',
    borderColor: 'border-purple-100 dark:border-purple-900/30',
    items: [
      { id: 'web_manager', label: '🖼️ ปรับแต่งหน้าเว็บ (CMS)' },
      { id: 'seo', label: '🌐 ตั้งค่าระบบ SEO หน้าเว็บ' },
      { id: 'chat', label: '💬 ระบบรวมแชทลูกค้า (Chat)' },
      { id: 'facebook_automation', label: '📢 โพสต์แฟนเพจอัตโนมัติ' }
    ]
  },
  {
    title: '⚙️ ระบบบุคคล & จัดการ IT (HR, Roles & IT Systems)',
    bgColor: 'bg-amber-50/40 dark:bg-amber-950/10',
    borderColor: 'border-amber-100 dark:border-amber-900/30',
    items: [
      { id: 'employees', label: '👥 ข้อมูลพนักงานทั่วไป' },
      { id: 'hr', label: '⏰ ฝ่ายบุคคล & ขาดลาสาย' },
      { id: 'kpi', label: '🎯 ประเมินผลงาน & KPI' },
      { id: 'roles', label: '🔐 จัดการสิทธิ์ & ไอดี (Roles)' },
      { id: 'logs', label: '📝 ระบบ Log การใช้งาน' },
      { id: 'helpdesk', label: '🛟 แจ้งขอเพิ่มระบบ (Tickets)' }
    ]
  }
];

export default function RolesManagementPage() {
  const [users, setUsers] = useState([]); 
  const [editUid, setEditUid] = useState(null); // 🚀 สถานะว่ากำลัง "แก้ไข" ใครอยู่ไหม
  
  const [formData, setFormData] = useState({ 
    name: '', username: '', password: '', role: 'tech', 
    department: 'ทีมช่างซ่อม', 
    permissions: ['dashboard'] 
  });

  useEffect(() => {
    fetchUsersFromDB();
  }, []);

  const fetchUsersFromDB = async () => {
    try {
      const response = await fetch(`${API_URL}?action=get_users`);
      const data = await response.json();
      setUsers(data); 
    } catch (error) {
      console.error("ดึงข้อมูลไม่ได้:", error);
    }
  };

  const handlePermissionChange = (menuId) => {
    setFormData(prev => {
      const isChecked = prev.permissions.includes(menuId);
      if (isChecked) {
        return { ...prev, permissions: prev.permissions.filter(id => id !== menuId) };
      } else {
        return { ...prev, permissions: [...prev.permissions, menuId] };
      }
    });
  };

  const toggleGroupPermissions = (groupItems) => {
    const itemIds = groupItems.map(item => item.id);
    const allChecked = itemIds.every(id => formData.permissions.includes(id));
    setFormData(prev => {
      let newPermissions;
      if (allChecked) {
        newPermissions = prev.permissions.filter(id => !itemIds.includes(id));
      } else {
        newPermissions = [...new Set([...prev.permissions, ...itemIds])];
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  // 🗑️ ฟังก์ชันลบผู้ใช้
  const handleDeleteClick = (uid, name) => {
    Swal.fire({
      title: `ลบบัญชี ${name}?`,
      text: "การลบจะไม่สามารถกู้คืนข้อมูลได้",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ยืนยันการลบ',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({ title: 'กำลังลบ...', didOpen: () => Swal.showLoading() });
        try {
          const res = await fetch(`${API_URL}?action=delete_user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid })
          });
          const data = await res.json();
          if (data.success) {
            Swal.fire('ลบสำเร็จ!', '', 'success');
            fetchUsersFromDB();
          } else {
            Swal.fire('ผิดพลาด', data.message, 'error');
          }
        } catch (error) {
          Swal.fire('ผิดพลาด', 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้', 'error');
        }
      }
    });
  };

  // ✏️ ฟังก์ชันเมื่อกดปุ่มแก้ไข
  const handleEditClick = (user) => {
    setEditUid(user.uid);
    
    // แปลงข้อมูลสิทธิ์จาก String ใน DB ให้เป็น Array
    let parsedPermissions = ['dashboard'];
    if (user.permissions) {
      try { parsedPermissions = JSON.parse(user.permissions); } catch (e) {}
    }

    setFormData({
      name: user.name,
      username: user.username,
      password: '', // ปล่อยว่างไว้ ถ้าไม่กรอกแปลว่าไม่เปลี่ยนรหัส
      role: user.role,
      department: user.department || 'ทีมช่างซ่อม',
      permissions: parsedPermissions
    });
    
    // เลื่อนหน้าจอกลับไปข้างบนสุดเพื่อแก้ฟอร์ม
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ❌ ยกเลิกการแก้ไข
  const cancelEdit = () => {
    setEditUid(null);
    setFormData({ name: '', username: '', password: '', role: 'tech', department: 'ทีมช่างซ่อม', permissions: ['dashboard'] });
  };

  // 💾 ฟังก์ชันบันทึกข้อมูล (ใช้ร่วมกันทั้ง สร้างใหม่ และ แก้ไข)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.username) {
      Swal.fire('แจ้งเตือน', 'กรุณากรอกชื่อและไอดีให้ครบถ้วน', 'warning');
      return;
    }
    // ถ้าสร้างใหม่ ต้องบังคับกรอกรหัสผ่าน
    if (!editUid && !formData.password) {
      Swal.fire('แจ้งเตือน', 'กรุณาตั้งรหัสผ่านสำหรับไอดีใหม่ด้วยครับ', 'warning');
      return;
    }
    if (formData.permissions.length === 0) {
      Swal.fire('แจ้งเตือน', 'กรุณาเลือกสิทธิ์การเข้าถึงอย่างน้อย 1 เมนูครับ', 'warning');
      return;
    }

    Swal.fire({ title: 'กำลังบันทึก...', didOpen: () => Swal.showLoading() });

    try {
      // เช็คว่าเป็นการสร้างใหม่ หรือ แก้ไข
      const actionUrl = editUid ? `${API_URL}?action=edit_user` : `${API_URL}?action=create_user`;
      const payload = editUid ? { ...formData, uid: editUid } : formData;

      const res = await fetch(actionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await res.json();

      if (result.success) {
        Swal.fire('สำเร็จ!', editUid ? 'อัปเดตข้อมูลเรียบร้อย!' : 'สร้างบัญชีเรียบร้อย!', 'success');
        cancelEdit(); // ล้างฟอร์ม
        fetchUsersFromDB(); // โหลดตารางใหม่
      } else {
        Swal.fire('เกิดข้อผิดพลาด', result.message || 'ไม่สามารถดำเนินการได้', 'error');
      }
    } catch (error) {
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้', 'error');
    }
  };

  return (
    <div className="space-y-6 pb-10">
      <div>
        <h1 className="text-2xl font-black text-gray-800 dark:text-white transition-colors">🔐 จัดการสิทธิ์ & สร้างไอดี</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 transition-colors">สร้างบัญชีพนักงาน กำหนดแผนก และเลือกเมนูที่อนุญาตให้ใช้งาน</p>
      </div>

      {/* 🟢 ส่วนที่ 1: ฟอร์มสร้าง/แก้ไขไอดี */}
      <div className={`p-6 rounded-xl border shadow-sm dark:shadow-lg transition-colors ${editUid ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800/30' : 'bg-white border-gray-200 dark:bg-[#1e293b] dark:border-[#334155]'}`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-base font-bold flex items-center gap-2 ${editUid ? 'text-orange-600 dark:text-orange-400' : 'text-gray-800 dark:text-white'}`}>
            {editUid ? <><FiEdit2 /> แก้ไขข้อมูลพนักงาน</> : <><FiUserPlus className="text-blue-500" /> ข้อมูลพนักงานใหม่</>}
          </h2>
          {editUid && (
            <button onClick={cancelEdit} className="text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-lg flex items-center gap-1 transition">
              <FiX /> ยกเลิกการแก้ไข
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ข้อมูลพื้นฐาน */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">ชื่อ - นามสกุล</label>
              <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg focus:border-blue-500 outline-none transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">ไอดี (Username)</label>
              <input required type="text" value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} disabled={editUid !== null} className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg focus:border-blue-500 outline-none transition-colors disabled:opacity-50" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">รหัสผ่าน {editUid && <span className="text-orange-500 font-normal">(เว้นว่างถ้าไม่เปลี่ยน)</span>}</label>
              <input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} placeholder={editUid ? '********' : ''} className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg focus:border-blue-500 outline-none transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">บทบาท (Role)</label>
              <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg focus:border-blue-500 outline-none transition-colors font-bold text-blue-600 dark:text-blue-400">
                <option value="tech">👨‍🔧 ช่างเทคนิค (ใช้งานแอปช่าง)</option>
                <option value="admin">💻 แอดมินหลังบ้าน (Admin)</option>
                <option value="sales">💼 ฝ่ายขาย / เซลล์ (Sales Console)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">แผนก / ตำแหน่ง</label>
              <select value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] text-gray-900 dark:text-white text-sm rounded-lg focus:border-blue-500 outline-none transition-colors">
                <option value="ทีมช่างซ่อม">ทีมช่างซ่อม</option>
                <option value="ทีมประเมินราคา">ทีมประเมินราคา</option>
                <option value="แอดมิน (Admin)">แอดมิน (Admin)</option>
                <option value="ฝ่ายบริการลูกค้า">ฝ่ายบริการลูกค้า</option>
                <option value="บัญชี/การเงิน">บัญชี/การเงิน</option>
                <option value="ฝ่ายขาย (Sales)">ฝ่ายขาย (Sales)</option>
              </select>
            </div>
          </div>

          {/* สิทธิ์การเข้าถึงเมนูแบบจัดกลุ่มหมวดหมู่ (Grouped Permissions) */}
          <div className="pt-6 border-t border-gray-100 dark:border-[#334155] space-y-6">
            <label className="block text-sm font-black text-gray-800 dark:text-white mb-2 flex items-center gap-2">
              <FiCheckSquare className="text-blue-500" size={18} /> กำหนดสิทธิ์การเข้าถึงระบบแยกตามหมวดหมู่การทำงาน
            </label>
            
            <div className="grid grid-cols-1 gap-5">
              {PERMISSION_GROUPS.map((group, gIdx) => {
                const groupItemIds = group.items.map(item => item.id);
                const allChecked = groupItemIds.every(id => formData.permissions.includes(id));
                const someChecked = groupItemIds.some(id => formData.permissions.includes(id)) && !allChecked;

                return (
                  <div key={gIdx} className={`p-4 rounded-xl border transition-all ${group.bgColor} ${group.borderColor} shadow-sm`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 mb-3 border-b border-gray-200/55 dark:border-gray-800/55">
                      <h3 className="text-xs font-bold text-gray-800 dark:text-white flex items-center gap-1.5">
                        <span className="w-1.5 h-3 bg-blue-500 rounded-full inline-block"></span>
                        {group.title}
                      </h3>
                      <button
                        type="button"
                        onClick={() => toggleGroupPermissions(group.items)}
                        className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border transition-all cursor-pointer ${
                          allChecked 
                            ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700' 
                            : someChecked
                            ? 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100 dark:bg-blue-950/30 dark:border-blue-900/50 dark:text-blue-400'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {allChecked ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด'}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {group.items.map(item => {
                        const isChecked = formData.permissions.includes(item.id);
                        return (
                          <label 
                            key={item.id} 
                            className={`flex items-center gap-2 cursor-pointer p-2.5 rounded-lg border transition-all ${
                              isChecked 
                                ? 'bg-white dark:bg-gray-800 border-blue-500 shadow-sm dark:shadow-none ring-1 ring-blue-500/10' 
                                : 'bg-white/50 dark:bg-[#0f172a]/50 border-gray-250 dark:border-[#334155] hover:border-gray-350 dark:hover:border-gray-600'
                            }`}
                          >
                            <input 
                              type="checkbox" 
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 bg-gray-50 border-gray-300 transition cursor-pointer" 
                              checked={isChecked}
                              onChange={() => handlePermissionChange(item.id)}
                            />
                            <span className={`text-xs font-bold transition-colors ${isChecked ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'}`}>
                              {item.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button type="submit" className={`px-8 py-3 font-bold rounded-lg text-sm transition-all shadow-md text-white ${editUid ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
              {editUid ? '💾 บันทึกการแก้ไข' : 'บันทึกสร้างไอดีใหม่'}
            </button>
          </div>
        </form>
      </div>

      {/* 🟢 ส่วนที่ 2: ตารางแสดงข้อมูลจริง */}
      <div className="bg-white dark:bg-[#1e293b] p-6 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm dark:shadow-lg transition-colors">
        <h2 className="text-base font-bold mb-4 text-gray-800 dark:text-white flex items-center gap-2 transition-colors">
          <FiShield className="text-blue-500" /> บัญชีผู้ใช้งานในระบบ
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
            <thead className="text-xs uppercase bg-gray-100 dark:bg-[#0f172a] text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-[#334155] transition-colors">
              <tr>
                <th className="px-4 py-3 font-medium">ชื่อพนักงาน</th>
                <th className="px-4 py-3 font-medium">ไอดี (Username)</th>
                <th className="px-4 py-3 font-medium">บทบาท / แผนก</th>
                <th className="px-4 py-3 font-medium text-center">จัดการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-[#334155] transition-colors">
              {users.length > 0 ? users.map((u, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-[#0f172a]/50 transition-colors">
                  <td className="px-4 py-3 text-gray-900 dark:text-white font-bold">{u.name}</td>
                  <td className="px-4 py-3 text-blue-600 dark:text-blue-400 font-medium">{u.username}</td>
                  <td className="px-4 py-3 space-x-1.5">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border inline-block ${u.role === 'tech' ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/20 dark:text-orange-400 dark:border-orange-900/30' : u.role === 'sales' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/20 dark:text-indigo-400 dark:border-indigo-900/30' : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900/30'}`}>
                      {u.role === 'tech' ? '👨‍🔧 ช่าง' : u.role === 'sales' ? '💼 เซลส์' : '💻 แอดมิน'}
                    </span>
                    <span className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded text-[10px] font-bold border border-gray-200 dark:border-gray-700 inline-block">
                      {u.department || 'ทั่วไป'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleEditClick(u)} className="text-gray-400 hover:text-yellow-500 dark:hover:text-yellow-400 mx-2 transition-colors"><FiEdit2 size={16} /></button>
                    <button onClick={() => handleDeleteClick(u.uid, u.name)} className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 mx-2 transition-colors"><FiTrash2 size={16} /></button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="text-center py-10 text-gray-500 font-medium">กำลังเชื่อมต่อฐานข้อมูล... หรือยังไม่มีข้อมูลในระบบ</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}